/**
 * Store Pinia des bains diluteurs de production (composition API)
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ServiceOperationResult } from '../models/api/serviceOperationResult';
import { IDataOperationResult } from '../models/api/dataOperationResult';
import DataOperationError from '../models/api/dataOperationError';
import { IProductionDilutorBath } from '../models/productionDilutorBath';
import { IProductionDilutorBathApiResponse } from '../models/api/productionDilutorBathApiResponse';
import { IProductionDilutorBathForm } from '../models/forms/productionDilutorBathForm';
import { IProductionDilutorBathFilter } from '../models/filters/productionDilutorBathFilter';
import ProductionDilutorBathServices from '../services/ProductionDilutorBathService';
import { useProductionStore } from './production.store';
import { useDilutorBathStore } from './dilutorBath.store';
import { useDeviceStore } from './device.store';
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des bains diluteurs de production utilisant la Composition API
 */
export const useProductionDilutorBathStore = defineStore('ProductionDilutorBaths', () => {
    // State
    const datas = ref<IProductionDilutorBath[]>([]);
    const current = ref<IProductionDilutorBath>({} as IProductionDilutorBath);
    const itemForm = ref<IProductionDilutorBathForm>({} as IProductionDilutorBathForm);

    // Actions

    /**
     * Hydrate une liste de bains diluteurs en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param dilutorBaths Liste de bains diluteurs reçus de l'API avec des IDs
     * @returns Liste de bains diluteurs avec les références des stores
     */
    async function hydrateDilutorBaths(dilutorBaths: IProductionDilutorBathApiResponse[]): Promise<IProductionDilutorBath[]> {
        if (dilutorBaths.length === 0) return [];

        // NOTE: Ne PAS charger productionStore ici pour éviter la boucle circulaire
        const dilutorBathStore = useDilutorBathStore();
        const deviceStore = useDeviceStore();

        // dilutorBathStore et deviceStore sont garantis chargés au démarrage (storeLoader Phase 3).
        // Le fallback de chargement ici est supprimé car deviceStore.load() est bloqué hors contexte main.ts.
        if (dilutorBathStore.datas.length === 0) {
            await dilutorBathStore.load({ page: 1, pageSize: 0 });
        }

        // Maps O(1)
        const dilutorBathMap = new Map(dilutorBathStore.datas.map(d => [d.id, d]));
        const deviceMap = new Map(deviceStore.datas.map((d: any) => [d.id, d]));

        // Hydrater en une seule passe
        const models: IProductionDilutorBath[] = [];
        for (const bath of dilutorBaths) {
            const model = { ...bath } as unknown as IProductionDilutorBath;
            // NOTE: model.production n'est pas hydraté ici pour éviter la référence circulaire
            model.dilutorBath = dilutorBathMap.get(bath.dilutorBathId) ?? model.dilutorBath;
            model.pumpDevice = bath.pumpDeviceId ? deviceMap.get(bath.pumpDeviceId) ?? model.pumpDevice : model.pumpDevice;
            models.push(model);
        }
        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     * @returns Le résultat de l'opération
     */
    async function load(filter: IProductionDilutorBathFilter) {
        const result = await ProductionDilutorBathServices.getAll<IProductionDilutorBathFilter, IProductionDilutorBathApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateDilutorBaths(result.data as IProductionDilutorBathApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id L'identifiant de l'élément à récupérer
     * @returns Le résultat de l'opération
     */
    async function get(id: number) {
        const result = await ProductionDilutorBathServices.getById<IProductionDilutorBathApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateDilutorBaths([result.data as IProductionDilutorBathApiResponse]);
            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Sauvegarde d'un élément
     * @returns Le résultat de l'opération
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<IProductionDilutorBathApiResponse | IDataOperationResult<IProductionDilutorBathApiResponse>>;
        if (itemForm.value.id > 0) {
            result = await ProductionDilutorBathServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDilutorBaths([result.data as IProductionDilutorBathApiResponse]);
                current.value = modelData;
                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = 'Item non trouvé dans le store';
                    return new ServiceOperationResult<IDataOperationResult<IProductionDilutorBath>>(true, { errors: error });
                }
            }
        } else {
            result = await ProductionDilutorBathServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDilutorBaths([result.data as IProductionDilutorBathApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    /**
     * Mappage de l'élément current en formulaire
     * @param item L'élément à mapper
     * @returns Le formulaire mappé
     */
    function toForm(item: IProductionDilutorBath): IProductionDilutorBathForm {
        const form = {} as IProductionDilutorBathForm;
        form.id = item.id;
        form.productionId = item.productionId;
        form.dilutorBathId = item.dilutorBathId;
        form.pumpDeviceId = item.pumpDeviceId;
        form.volume = item.volume;
        form.dateVolume = item.dateVolume;
        form.isPreDilution = item.isPreDilution;
        return form;
    }

    /**
     * Création d'un nouvel élément en formulaire
     */
    function newForm(lienId: number): IProductionDilutorBathForm {
        itemForm.value = {
            id: 0,
            productionId: lienId,
            dilutorBathId: 0,
            volume: 0,
            dateVolume: new Date(),
            isPreDilution: false
        } as IProductionDilutorBathForm;
        return itemForm.value;
    }

    /**
     * Création d'un nouvel item
     */
    function newItem(): IProductionDilutorBath {
        current.value = {
            id: 0,
            productionId: 0,
            production: {} as any,
            dilutorBathId: 0,
            dilutorBath: {} as any,
            pumpDevice: undefined as any,
            volume: 0,
            dateVolume: new Date(),
            isPreDilution: false
        } as IProductionDilutorBath;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,

        // Actions
        hydrateDilutorBaths,
        load,
        get,
        save,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajProductionDilutorBath", async (payload: IPayloadStore) => {
    const store = useProductionDilutorBathStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateDilutorBaths([payload.data as IProductionDilutorBathApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateDilutorBaths([payload.data as IProductionDilutorBathApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
